import { Form } from '@ovComponents/2-component/form/form';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { SettingsCard } from '@ovComponents/3-pattern/settingsCard/settingsCard';
import { getReconFlagTypeLabel } from '@ovComponents/4-module/reconFlags/utils/reconFlags.config';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import { Alert, Box, Button, Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import type { ChangeEvent } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlagPolicyRowView } from './flagPolicies.types';
import { FlagPolicyRow } from './flagPolicyRow';
import { useFlagPolicyEditor } from './useFlagPolicyEditor';

const filterRows = (
  rows: FlagPolicyRowView[],
  searchText: string,
  t: TFunction,
): FlagPolicyRowView[] => {
  const query = searchText.trim().toLowerCase();
  if (!query) {
    return rows;
  }

  return rows.filter((row) => {
    const label = getReconFlagTypeLabel(t, row.flagType).toLowerCase();
    return label.includes(query) || row.flagType.toLowerCase().includes(query);
  });
};

export const FlagPoliciesSettings = () => {
  const { activeOrganization } = useContext(UserContext);
  const canReadFlags = usePermission('read:flags');
  const canWriteFlags = usePermission('write:flags');
  const { t } = useTranslation(['settings', 'shared', 'reconciliation']);

  const editor = useFlagPolicyEditor({
    organizationId: activeOrganization?.id,
    canRead: canReadFlags,
    canWrite: canWriteFlags,
  });

  const [searchText, setSearchText] = usePageState('', 'q');

  const filteredRows = useMemo(
    () => filterRows(editor.rows, searchText, t),
    [editor.rows, searchText, t],
  );

  const validationMessage = editor.validationErrorKey
    ? t(editor.validationErrorKey)
    : null;

  const canSubmit =
    canWriteFlags &&
    editor.isOrgSynced &&
    !editor.loading &&
    !editor.saving &&
    editor.isDirty &&
    !editor.validationErrorKey;

  if (!canReadFlags) {
    return (
      <SettingsCard
        title={t('flagPolicies.title')}
        description={t('flagPolicies.description')}
      >
        <Alert severity='warning'>{t('flagPolicies.noAccess')}</Alert>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      title={t('flagPolicies.title')}
      description={t('flagPolicies.description')}
    >
      <Form onSubmit={editor.save}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                height: '40px',
                flexGrow: 1,
                '& .MuiAlert-root': {
                  height: '100%',
                  py: 0,
                  boxSizing: 'border-box',
                  alignItems: 'center',
                },
              }}
            >
              <Alert severity='info'>
                {t('flagPolicies.futureFlagsNotice')}
              </Alert>
            </Box>

            <TextField
              testId='settings-flag-policies-search'
              label=''
              value={searchText}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearchText(event.target.value);
              }}
              leadingIcon='search'
              placeholder={t('flagPolicies.searchPlaceholder')}
            />
          </Box>

          {validationMessage && (
            <Alert severity='error'>{validationMessage}</Alert>
          )}

          {editor.loading && (
            <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
              {t('shared:loading')}
            </Typography>
          )}

          {!editor.loading && filteredRows.length === 0 && (
            <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
              {t('flagPolicies.noResults')}
            </Typography>
          )}

          {!editor.loading &&
            filteredRows.map((row) => (
              <FlagPolicyRow
                key={row.flagType}
                row={row}
                disabled={!canWriteFlags || editor.saving}
                onEnabledChange={editor.setEnabledControlState}
                onPriorityChange={editor.setPriority}
                onThresholdCentsChange={editor.setThresholdCents}
                onPercentThresholdChange={editor.setPercentThreshold}
                testId={`settings-flag-policies-${row.flagType}`}
              />
            ))}

          {canWriteFlags && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 1,
              }}
            >
              <Button
                type='button'
                variant='outlined'
                disabled={!editor.isDirty || editor.saving}
                onClick={editor.cancel}
                data-testid='settings-flag-policies-cancel'
              >
                {t('shared:cancel')}
              </Button>
              <Button
                type='submit'
                disabled={!canSubmit}
                loading={editor.saving}
                data-testid='settings-flag-policies-save'
              >
                {t('shared:save')}
              </Button>
            </Box>
          )}
        </Box>
      </Form>
    </SettingsCard>
  );
};
